import React, { useEffect, useState } from 'react';
import { Link as ReactLink, useLocation, matchPath } from 'react-router-dom';
import { VisAuthService } from '@vis-auth/react';
import { Footer as VerUDSFooter, Button } from '@ver-uds/react';
import dhsSeal from '../images/dhs-seal.svg';
import { getAuthLink, isToggleEnabled } from '@save/utils';
import { useTranslation } from 'react-i18next';
import { useSwitchLanguage } from '../localization/useSwitchLanguage';

const DHS_URL = 'https://www.dhs.gov/';
const USCIS_URL = 'https://www.uscis.gov/';

const ACCESSIBILITY_URL = 'https://www.uscis.gov/website-policies/accessibility';
const PLUGINS_URL = 'https://www.uscis.gov/website-policies/plug-ins';
const BROWSER_SUPPORT_URL = '/save/app/client/ui/supported-browsers';
const SITE_MAP = '/save/app/client/ui/help/site-map';
const ICA_PATH = '/save/app/static2/ica-documentation/index.html';
const FEEDBACK_URL = 'mailto:savesystemfeedback@uscis.dhs.gov';

export const Link = (url: string, title: string, external: boolean = false): JSX.Element => (
  <>
    {external === true ? (
      <a href={url} className="usa-link" data-testid="external-link">
        {title}
      </a>
    ) : (
      <ReactLink to={url} className="usa-link" data-testid="relative-link">
        {title}
      </ReactLink>
    )}
  </>
);

const buildRightSection = (authService, isCaseCheck, t) => {
  return isCaseCheck ? (
    <VerUDSFooter.Nav>{Link(ACCESSIBILITY_URL, t('footer.accessibility'), true)}</VerUDSFooter.Nav>
  ) : (
    <VerUDSFooter.Nav>
      {Link(ACCESSIBILITY_URL, 'Accessibility', true)}
      {Link(BROWSER_SUPPORT_URL, 'Browser Support')}
      {Link(PLUGINS_URL, 'Plug-ins', true)}
      {Link(SITE_MAP, 'Site Map')}
      <a
        className="usa-link"
        href="#"
        onClick={(e) => {
          const { accessToken } = authService.state;
          getAuthLink(e, '/documents/7731c4c3-7859-4690-ad6a-dedd90bf4417/content', accessToken);
        }}
      >
        {isToggleEnabled('LIVE_ICA_FOOTER_LINK') ? 'V37.1 ICA' : 'ICA'}
      </a>
      {isToggleEnabled('LIVE_ICA_FOOTER_LINK') ? (
        <a target="_blank" className="usa-link" href={ICA_PATH}>
          V38 ICA - Draft
        </a>
      ) : (
        ''
      )}
    </VerUDSFooter.Nav>
  );
};

export default function Footer({ authService }: { authService?: VisAuthService }) {
  const [isCaseCheck, setIsCaseCheck] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  useSwitchLanguage();

  useEffect(() => {
    const matchResult = matchPath(location.pathname, {
      path: '*/case-check*',
    });
    matchResult != null ? setIsCaseCheck(true) : setIsCaseCheck(false);
  }, [location]);

  return (
    <VerUDSFooter
      primarySection={
        <VerUDSFooter.Section
          leftSection={
            <VerUDSFooter.Nav>
              {Link(DHS_URL, t('footer.dhs'), true)}
              {Link(USCIS_URL, t('footer.uscis'), true)}
            </VerUDSFooter.Nav>
          }
          rightSection={buildRightSection(authService, isCaseCheck, t)}
        />
      }
      secondarySection={
        <VerUDSFooter.Section
          leftSection={
            <VerUDSFooter.Nav>
              <img src={dhsSeal} alt="dhs-seal" />
            </VerUDSFooter.Nav>
          }
          rightSection={
            <VerUDSFooter.Nav>
              <Button className="margin-0" href={FEEDBACK_URL} as="a">
                {t('footer.feedback')}
              </Button>
            </VerUDSFooter.Nav>
          }
        />
      }
    />
  );
}
